/*
 * Trinom Digital Pvt Ltd ("COMPANY") CONFIDENTIAL                             *
 * Copyright (c) 2024 Trinom Digital Pvt Ltd, All rights reserved              *
 *                                                                             *
 * NOTICE:  All information contained herein is, and remains the property      *
 * of COMPANY. The intellectual and technical concepts contained herein are    *
 * proprietary to COMPANY and may be covered by Indian and Foreign Patents,    *
 * patents in process, and are protected by trade secret or copyright law.     *
 * Dissemination of this information or reproduction of this material is       *
 * strictly forbidden unless prior written permission is obtained from         *
 * COMPANY. Access to the source code contained herein is hereby forbidden     *
 * to anyone except current COMPANY employees, managers or contractors who     *
 * have executed Confidentiality and Non-disclosure agreements explicitly      *
 * covering such access.                                                       *
 *                                                                             *
 * The copyright notice above does not evidence any actual or intended         *
 * publication or disclosure of this source code, which includes               *
 * information that is confidential and/or proprietary, and is a trade secret, *
 * of COMPANY. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC            *
 * PERFORMANCE, OR PUBLIC DISPLAY OF OR THROUGH USE OF THIS SOURCE CODE        *
 * WITHOUT THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED,      *
 * AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE         *
 * RECEIPT OR POSSESSION OF THIS SOURCE CODE AND/OR RELATED INFORMATION DOES   *
 * NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS     *
 * CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,    *
 * IN WHOLE OR IN PART.                                                        *
 *                                                                             *
 * File: \src\main.js                                                          *
 * Project: frontend                                                           *
 * Created Date: Tuesday, November 7th 2023, 6:08:01 pm                        *
 * Author: Sankarra Narayanan G <sankar@codestax.ai>                           *
 * -----                                                                       *
 * Last Modified: December 11th 2024, 10:07:44 am                              *
 * Modified By: Sankarra Narayanan G                                           *
 * -----                                                                       *
 * Any app that can be written in JavaScript,                                  *
 *     will eventually be written in JavaScript !!                             *
 * -----                                                                       *
 * HISTORY:                                                                    *
 * Date         By  Comments                                                   *
 * --------------------------------------------------------------------------- *
 */

import Vue from 'vue'
import App from './App.vue'
import router from './router'
import VueCookie from "vue-cookie";
import "@/assets/styles/main.css";
import vuetify from './plugins/vuetify'
import 'vuetify/dist/vuetify.min.css'
import VuePhoneNumberInput from 'vue-phone-number-input';
import DatetimePicker from 'vuetify-datetime-picker';
import 'vue-phone-number-input/dist/vue-phone-number-input.css';
import i18n from './i18n'
import store from './store'
import JsonCSV from "vue-json-csv";
import moment from 'moment'
import VueExcelEditor from 'vue-excel-editor'
import VueNativeSock from 'vue-native-websocket'
import axios from 'axios';

Vue.component('downloadCSV', JsonCSV);
Vue.component('vue-phone-number-input', VuePhoneNumberInput);
Vue.use(VueCookie);
Vue.use(DatetimePicker);
Vue.use(VueExcelEditor);
Vue.use(VueNativeSock, `${process.env.VUE_APP_WS_URL}`, {
  connectManually: true,
})

Vue.prototype.moment = moment

Vue.config.productionTip = false

Vue.filter('fromNow', function (value) {
  if (!Number.isNaN(Number(value))) {
    return moment(value).fromNow()
  }
  return value
});

Vue.filter('ellipsis', function (value) {
  if (value.length > 10) {
    return `${value.slice(0, 7)}…`
  }
  return value
});

function fetchOrigin() {
  const frontendUrl = window.location.origin;
  if (!process.env.VUE_APP_FRONTEND_REVERSE_PROXY_URL || !process.env.VUE_APP_BACKEND_REVERSE_PROXY_URL) {
    return 'skip';
  }
  return frontendUrl;
}
function replaceHostName(config) {
  if (!config.url) {
    return config;
  }

  const originalUrl = config.url;
  const urlObject = new URL(originalUrl);

  // Determine backend base URL based on the hostname
  const s3CloudFrontUrl = process.env.VUE_APP_S3_FILES_CLOUDFRONT_URL;
  const backendReverseProxyUrl = process.env.VUE_APP_BACKEND_REVERSE_PROXY_URL;

  if (!s3CloudFrontUrl || !backendReverseProxyUrl || urlObject.origin == process.env.VUE_APP_S3_FILES_REVERSE_PROXY_URL) {
    return config;
  }

  let backendBaseUrl;
  if (urlObject.origin == s3CloudFrontUrl) {
    backendBaseUrl = process.env.VUE_APP_S3_REVERSE_PROXY_CLOUDFRONT_URL;
  } else {
    backendBaseUrl = backendReverseProxyUrl;
  }

  urlObject.hostname = new URL(backendBaseUrl).hostname;
  config.url = urlObject.toString();

  return config;
}

// Axios interceptor
axios.interceptors.request.use(
  (config) => {

    const frontendUrl = fetchOrigin();
    const frontendReverseProxyUrl = process.env.VUE_APP_FRONTEND_REVERSE_PROXY_URL;
    const s3Url = process.env.VUE_APP_S3_URL;
    const s3FilesReverseProxyUrl = process.env.VUE_APP_S3_REVERSE_PROXY_URL;

    // Skip processing if frontend URL doesn't match or is marked as 'skip'
    if (frontendUrl === 'skip' || frontendUrl !== frontendReverseProxyUrl) {
      return config;
    }

    // S3 URL case
    if (config.url === s3Url) {
      config.url = `${s3FilesReverseProxyUrl}`;
      return config;
    }

    // other url's
    config = replaceHostName(config);
    return config;
  },
  (error) => {
    console.error('Request Interceptor Error:', error);
    return Promise.reject(error);
  }
);


axios.interceptors.response.use((response) => {
  return response;
}, (error) => {
  return Promise.reject(error);
});
new Vue({
  router,
  vuetify,
  i18n,
  store,
  render: function (h) { return h(App) }
}).$mount('#app')
